<script setup lang="ts">
import type { MaskInputOptions } from "maska";

import { vMaska } from "maska/vue";
import { computed, onMounted, reactive, ref, useCssModule, watch } from "vue";

import Modal from "@/components/modal/modal.vue";
import { BugTrackerSection, useBugTracker } from "@/helpers/bugTracker";
import { destructureFIO } from "@/helpers/destructureFIO";
import parseStringPrice from "@/helpers/parseStringPrice";
import WIDGET_ANALYTICS_EVENTS from "@/helpers/widgetAnalyticsEvents/widgetAnalyticsEvents";
import createApplication from "@/hooks/requests/createApplication/createApplication";
import { useAnalytics } from "@/hooks/useAnalytics/useAnalytics";
import useStoreResetter from "@/hooks/useStoreResetter/useStoreResetter";
import useTranslate from "@/hooks/useTranslate/useTranslate";
import useValidate from "@/hooks/useValidate";
import { useChooseDeviceStore } from "@/stores/chooseDevice/chooseDevice";
import { useDeliveryStore } from "@/stores/delivery/delivery";
import { useHowYouRateDeviceStore } from "@/stores/howYouRateDevice/howYouRateDevice";
import { useSuccessfulEvaluationStore } from "@/stores/successfulEvaluation/successfulEvaluation";
import { useTIWStore } from "@/stores/tiw/tiw";
import Button from "@/ui/button/button.vue";
import Checkbox from "@/ui/checkbox/checkbox.vue";
import Field from "@/ui/field/field.vue";
import Input from "@/ui/input/input.vue";

import { getDeliveryDays } from "./thanksYou";

onMounted(() => {
    useAnalytics(WIDGET_ANALYTICS_EVENTS.PICKUP_ADDRESS);
});

const classes = useCssModule();

const TIWStore = useTIWStore();
const DeliveryStore = useDeliveryStore();
const SuccessfulEvaluationStore = useSuccessfulEvaluationStore();
const HowYouRateDeviceStore = useHowYouRateDeviceStore();
const ChooseDeviceStore = useChooseDeviceStore();

const pathToImages = import.meta.env.VITE_APP_URL;

const isSameAddress = ref(false);
const loadingToNext = ref(false);

const howFindImeiBlock = ref<boolean>(false);

const handedDevicePrice = computed(() => {
    if (typeof HowYouRateDeviceStore?.calculatedDeviceData?.panelTradeInSale === "object") {
        return HowYouRateDeviceStore?.calculatedDeviceData?.panelTradeInSale?.tradeInSale;
    }

    return HowYouRateDeviceStore?.calculatedDeviceData?.panelTradeInSale;
});

const successfulEvaluationDestructuredFIO = computed(() =>
    destructureFIO(SuccessfulEvaluationStore.fields.fio),
);

function setArrivalDate(date: string) {
    DeliveryStore.deliveryInfo.arrivalDate = date;
}

async function onNext() {
    loadingToNext.value = true;

    try {
        const deliveryAddress =
            `${DeliveryStore.deliveryInfo.docStreet} ${DeliveryStore.deliveryInfo.docHouseNum}, apt. ${DeliveryStore.deliveryInfo.docApartmentNum}`.trim();
        const address =
            `${SuccessfulEvaluationStore.fields.docStreet} ${SuccessfulEvaluationStore.fields.docHouseNum}, apt. ${SuccessfulEvaluationStore.fields.docApartmentNum}`.trim();

        await createApplication({
            device_id: HowYouRateDeviceStore.registeringDeviceData.deviceId,
            ex_id: HowYouRateDeviceStore.registeringDeviceData.deviceId,
            imei: currentDeviceValue.value,
            locale: TIWStore.standardizedLocale,
            location: TIWStore.WIDGET_DATA.location,
            source: TIWStore.WIDGET_DATA.code,
            phone: SuccessfulEvaluationStore.fields.phone.replace(/\D+/g, ""),
            city_name: SuccessfulEvaluationStore.fields.docCityName,
            email: SuccessfulEvaluationStore.fields.email,
            first_name: SuccessfulEvaluationStore.destructuredFio.firstName,
            last_name: SuccessfulEvaluationStore.destructuredFio.lastName,
            pickup_date: DeliveryStore.deliveryInfo.arrivalDate,
            contact_person: {
                address: deliveryAddress,
                city_name: DeliveryStore.deliveryInfo.docCityName,
                first_name: successfulEvaluationDestructuredFIO.value.firstName,
                last_name: successfulEvaluationDestructuredFIO.value.lastName,
                phone: DeliveryStore.deliveryInfo.phone.replace(/\D+/g, ""),
            },
            grade: HowYouRateDeviceStore.registeringDeviceData.grade,
            brand: ChooseDeviceStore.brandName,
            model: ChooseDeviceStore.field_model.value.name,
            price: parseStringPrice(handedDevicePrice.value),
            address,
        });

        TIWStore.switchStep("thanksForOrder");
    } catch (e) {
        useBugTracker(
            "Successful Evaluation --- [POST] Create Application",
            BugTrackerSection.ERROR,
            {
                message: e?.message,
            },
        );
        loadingToNext.value = false;
        return;
    }

    if (!TIWStore.locationIsZA) {
        TIWStore.INJECTED_TIW.current_api.onDrop();
        TIWStore.closeWidget();
        TIWStore.switchStep("chooseDevice");
        useStoreResetter();
    }
}

const phoneMaskOptions = reactive<MaskInputOptions>({
    mask: TIWStore.phoneMask,
    tokens: {
        _: {
            pattern: /\d/,
        },
        A: {
            pattern: /[1-9]/,
        },
    },
    eager: true,
});

const arrivalDates = getDeliveryDays().map((el) => {
    return { date: el.date, dateLabel: el.label, timeStart: "08:00", timeEnd: "18:00" };
});

watch(isSameAddress, () => {
    if (isSameAddress.value) {
        DeliveryStore.deliveryInfo.fio = SuccessfulEvaluationStore.fields.fio;
        DeliveryStore.deliveryInfo.docApartmentNum =
            SuccessfulEvaluationStore.fields.docApartmentNum;
        DeliveryStore.deliveryInfo.docCityName = SuccessfulEvaluationStore.fields.docCityName;
        DeliveryStore.deliveryInfo.phone = SuccessfulEvaluationStore.fields.phone;
        DeliveryStore.deliveryInfo.email = SuccessfulEvaluationStore.fields.email;
        DeliveryStore.deliveryInfo.docStreet = SuccessfulEvaluationStore.fields.docStreet;
        DeliveryStore.deliveryInfo.docPostCode = SuccessfulEvaluationStore.fields.docPostCode;
        DeliveryStore.deliveryInfo.inn = SuccessfulEvaluationStore.fields.inn;
        DeliveryStore.deliveryInfo.docDistrict = SuccessfulEvaluationStore.fields.docDistrict;
        DeliveryStore.deliveryInfo.docNum = SuccessfulEvaluationStore.fields.docNum;
        DeliveryStore.deliveryInfo.docHouseNum = SuccessfulEvaluationStore.fields.docHouseNum;
        DeliveryStore.deliveryInfo.docApartmentNum =
            SuccessfulEvaluationStore.fields.docApartmentNum;
    } else {
        DeliveryStore.deliveryInfo.fio = "";
        DeliveryStore.deliveryInfo.docApartmentNum = "";
        DeliveryStore.deliveryInfo.docCityName = "";
        DeliveryStore.deliveryInfo.phone = "";
        DeliveryStore.deliveryInfo.email = "";
        DeliveryStore.deliveryInfo.docStreet = "";
        DeliveryStore.deliveryInfo.docPostCode = "";
        DeliveryStore.deliveryInfo.inn = "";
        DeliveryStore.deliveryInfo.docNum = "";
        DeliveryStore.deliveryInfo.docHouseNum = "";
        DeliveryStore.deliveryInfo.docDistrict = "";
    }
});

const showInfoAboutImei = () => {
    howFindImeiBlock.value = !howFindImeiBlock.value;
};

const phoneValidation = computed(() => {
    if (!DeliveryStore.deliveryInfo.phone) {
        return {
            status: true,
            message: "ok",
        };
    }
    const valiDate = useValidate({
        target: "phone",
        value: DeliveryStore.deliveryInfo.phone,
    });
    return valiDate;
});
const EnterImeiValidation = computed(() => {
    if (!currentDeviceValue.value) {
        return {
            status: true,
            message: "ok",
        };
    }
    const validate = useValidate({
        target: ChooseDeviceStore.isImei ? "imei" : "serial",
        value: currentDeviceValue.value,
    });
    return validate;
});
const fioValidation = computed(() => {
    if (!SuccessfulEvaluationStore.fields.fio) {
        return {
            status: true,
            message: "ok",
        };
    }
    const valiDate = useValidate({
        target: "fio",
        value: SuccessfulEvaluationStore.fields.fio,
    });
    return valiDate;
});
const innValidation = computed(() => {
    if (!SuccessfulEvaluationStore.fields.inn) {
        return {
            status: true,
            message: "ok",
        };
    }
    const valiDate = useValidate({
        target: "inn",
        value: SuccessfulEvaluationStore.fields.inn,
    });
    return valiDate;
});
const docNumValidation = computed(() => {
    if (!SuccessfulEvaluationStore.fields.docNum) {
        return {
            status: true,
            message: "ok",
        };
    }
    const valiDate = useValidate({
        target: TIWStore.locationIsZA ? "id-number--location-za" : "serias-and-number-md",
        value: SuccessfulEvaluationStore.fields.docNum,
    });
    return valiDate;
});
const docApartmentNumValidation = computed(() => {
    if (!SuccessfulEvaluationStore.fields.docApartmentNum) {
        return {
            status: true,
            message: "ok",
        };
    }
    return {
        status: SuccessfulEvaluationStore.fields.docApartmentNum.length > 0,
        message: useTranslate("tiw__validation__required"),
    };
});

const seriasAndNumberMDMaskOption = reactive<MaskInputOptions>({
    mask: TIWStore.seriasAndNumberMDMask,
    tokens: {
        Z: {
            pattern: /[a-zA-Z]/,
            transform: (value) => value.toUpperCase(),
        },
        _: {
            pattern: /[0-9]/,
        },
    },
    eager: true,
});
const docHouseNumValidation = computed(() => {
    if (!SuccessfulEvaluationStore.fields.docHouseNum) {
        return {
            status: true,
            message: "ok",
        };
    }
    return {
        status: SuccessfulEvaluationStore.fields.docHouseNum.length > 0,
        message: useTranslate("tiw__validation__required"),
    };
});
const docDistrictValidation = computed(() => {
    if (!SuccessfulEvaluationStore.fields.docDistrict) {
        return {
            status: true,
            message: "ok",
        };
    }
    return {
        status: SuccessfulEvaluationStore.fields.docDistrict.length > 0,
        message: useTranslate("tiw__validation__required"),
    };
});
const docPostCodeValidation = computed(() => {
    if (!SuccessfulEvaluationStore.fields.docPostCode) {
        return {
            status: true,
            message: "ok",
        };
    }
    return {
        status: SuccessfulEvaluationStore.fields.docPostCode.length > 0,
        message: useTranslate("tiw__validation__required"),
    };
});

const docStreetValidation = computed(() => {
    if (!SuccessfulEvaluationStore.fields.docStreet) {
        return {
            status: true,
            message: "ok",
        };
    }
    return {
        status: SuccessfulEvaluationStore.fields.docStreet.length > 0,
        message: useTranslate("tiw__validation__required"),
    };
});

const docCityNameValidation = computed(() => {
    if (!SuccessfulEvaluationStore.fields.docCityName) {
        return {
            status: true,
            message: "ok",
        };
    }
    return {
        status: SuccessfulEvaluationStore.fields.docCityName.length > 0,
        message: useTranslate("tiw__validation__required"),
    };
});

const passportIssueDateMaskOptions = reactive<MaskInputOptions>({
    mask: TIWStore.passportIssueDateMask,
    tokens: {
        _: {
            pattern: /[0-9]/,
        },
    },
    eager: true,
});
const passportIssueDateValidation = computed(() => {
    if (!SuccessfulEvaluationStore.fields.passportIssueDate) {
        return {
            status: true,
            message: "ok",
        };
    }
    const valiDate = useValidate({
        target: "human-date",
        value: SuccessfulEvaluationStore.fields.passportIssueDate,
    });
    return valiDate;
});
const emailValidation = computed(() => {
    if (!SuccessfulEvaluationStore.fields.email) {
        return {
            status: true,
            message: "ok",
        };
    }
    const valiDate = useValidate({
        target: "email",
        value: SuccessfulEvaluationStore.fields.email,
    });
    return valiDate;
});

const emailFieldPlaceholder = computed(() => {
    if (SuccessfulEvaluationStore.isEmailRequired) {
        return "";
    }

    return useTranslate("tiw__notRequired");
});

const currentDeviceValue = computed(() =>
    ChooseDeviceStore.isImei
        ? ChooseDeviceStore.field_imei.value
        : ChooseDeviceStore.field_serial.value,
);
</script>

<template>
    <Modal class="tiw__step__thanksYou">
        <template #header>
            <div :class="classes.header">
                <img :src="`${pathToImages}/img/success.svg`" alt="" />
                <p class="tiw__h2 classes.header__title">
                    {{ useTranslate("tiw__thanksYou__title") }}
                </p>
                <p :class="classes.header__desc">
                    {{ useTranslate("tiw__thanksYou__text") }}
                </p>
            </div>
        </template>
        <div :class="classes.body">
            <b
                ><p>{{ useTranslate("tiw__thanksYou__pleaseProvide") }}</p></b
            >
            <Checkbox v-model="isSameAddress" :class="classes.sameAddress" :checked="isSameAddress">
                {{ useTranslate("tiw__thanksYou_useTheSameAddress") }}
            </Checkbox>
            <form name="thanksYouForm">
                <Field :is-error="!EnterImeiValidation.status">
                    <template #title>
                        <span v-if="ChooseDeviceStore.isImei">
                            {{ useTranslate("tiw__enterImei__imei") }}
                        </span>
                        <span v-else>
                            {{ useTranslate("tiw__enterImei__serial") }}
                        </span>
                    </template>
                    <template #body>
                        <Input
                            v-model="currentDeviceValue"
                            type="text"
                            name="imei"
                            :disabled="!TIWStore.isDiscountLater || loadingToNext"
                            inputmode="numeric"
                            maxlength="15"
                        />
                    </template>
                </Field>
                <div
                    v-if="TIWStore.DISCOUNT_LATER"
                    class="tiw__step__successfulEvaluation__imei__infoBlock"
                >
                    <Button
                        color="link"
                        variant="link"
                        type="button"
                        class="tiw_fs-12"
                        @click="showInfoAboutImei"
                    >
                        <span>
                            {{
                                useTranslate(
                                    howFindImeiBlock ? "tiw__hide" : "tiw__enterImei__howFindImei",
                                )
                            }}
                        </span>
                    </Button>
                    <div v-if="howFindImeiBlock" class="tiw__step__enterImei__info">
                        <h3 class="tiw__title">
                            {{ useTranslate("tiw__enterImei__howFindImei") }}
                        </h3>
                        <ul class="tiw__ul tiw__ul_disc tiw_fs-14">
                            <li v-html="useTranslate('tiw__enterImei__howFindImei_step1')" />
                            <li>
                                {{ useTranslate("tiw__enterImei__howFindImei_step2") }}
                            </li>
                        </ul>
                        <div class="tiw__step__enterImei__info__images">
                            <figure class="tiw_tc">
                                <img
                                    width="100%"
                                    :src="`${pathToImages}/img/iphone_imei_1.webp`"
                                    alt="iPhone dial *#06#"
                                />
                            </figure>
                            <figure class="tiw_tc">
                                <img
                                    width="100%"
                                    :src="`${pathToImages}/img/iphone_imei_2.webp`"
                                    alt="iPhone dial *#06#"
                                />
                            </figure>
                        </div>
                    </div>
                </div>
                <!-- Поле "ФИО" -->
                <Field :is-error="!fioValidation.status">
                    <template #title>
                        <span>
                            {{
                                useTranslate(
                                    TIWStore.isDiscountLater
                                        ? "tiw__successfulEvaluation__contact_title"
                                        : "tiw__successfulEvaluation__fio_title",
                                )
                            }}
                        </span>
                    </template>
                    <template #hintTop>
                        <span>
                            {{
                                useTranslate(
                                    TIWStore.locationIsZA
                                        ? "tiw__successfulEvaluation__fio_location-za"
                                        : "tiw__successfulEvaluation__fio",
                                )
                            }}
                        </span>
                    </template>
                    <template #body>
                        <Input
                            v-model="DeliveryStore.deliveryInfo.fio"
                            type="text"
                            name="fio"
                            maxlength="50"
                            :disabled="loadingToNext"
                        />
                    </template>
                    <template v-if="!fioValidation.status" #hintBottom>
                        <span>
                            {{ fioValidation.message }}
                        </span>
                    </template>
                </Field>

                <!-- Поле "ИНН" -->
                <Field
                    v-if="!TIWStore.locationIsZA && !TIWStore.isDiscountLater"
                    :is-error="!innValidation.status"
                >
                    <template #hintTop>
                        <span v-if="TIWStore.locationIsMD">
                            {{ useTranslate("tiw__successfulEvaluation__idnp") }}
                        </span>
                        <span v-else>
                            {{ useTranslate("tiw__successfulEvaluation__inn") }}
                        </span>
                    </template>
                    <template #body>
                        <Input
                            v-model="DeliveryStore.deliveryInfo.inn"
                            type="text"
                            name="fio"
                            maxlength="12"
                            :disabled="loadingToNext"
                        />
                    </template>
                    <template v-if="!innValidation.status" #hintBottom>
                        <span>
                            {{ innValidation.message }}
                        </span>
                    </template>
                </Field>

                <!-- Поле "ID Number" -->
                <Field
                    v-if="TIWStore.locationIsZA || TIWStore.locationIsMD"
                    :is-error="!docNumValidation.status"
                >
                    <template #hintTop>
                        <span>
                            {{ useTranslate("tiw__successfulEvaluation__id_number") }}
                        </span>
                    </template>
                    <template #body>
                        <Input
                            v-if="TIWStore.locationIsZA"
                            v-model="DeliveryStore.deliveryInfo.docNum"
                            type="text"
                            name="docNum"
                            maxlength="13"
                            :disabled="loadingToNext"
                        />
                        <Input
                            v-else
                            v-model="DeliveryStore.deliveryInfo.docNum"
                            v-maska="seriasAndNumberMDMaskOption"
                            type="text"
                            name="docNum"
                            :disabled="loadingToNext"
                        />
                    </template>
                    <template v-if="!docNumValidation.status" #hintBottom>
                        <span>
                            {{ docNumValidation.message }}
                        </span>
                    </template>
                </Field>

                <!-- Поле "Номер квартиры" -->
                <Field
                    v-if="TIWStore.locationIsZA || TIWStore.locationIsMD"
                    :is-error="!docApartmentNumValidation.status"
                >
                    <template #hintTop>
                        <span>
                            {{ useTranslate("tiw__successfulEvaluation__apartment") }}
                        </span>
                    </template>
                    <template #body>
                        <Input
                            v-model="DeliveryStore.deliveryInfo.docApartmentNum"
                            type="text"
                            name="docApartmentNum"
                            :disabled="loadingToNext"
                            :placeholder="useTranslate('tiw__notRequired')"
                        />
                    </template>
                    <template v-if="!docApartmentNumValidation.status" #hintBottom>
                        <span>
                            {{ docApartmentNumValidation.message }}
                        </span>
                    </template>
                </Field>

                <!-- Поле "Номер дома" -->
                <Field
                    v-if="TIWStore.locationIsZA || TIWStore.locationIsMD"
                    :is-error="!docHouseNumValidation.status"
                >
                    <template #hintTop>
                        <span>
                            {{ useTranslate("tiw__successfulEvaluation__house") }}
                        </span>
                    </template>
                    <template #body>
                        <Input
                            v-model="DeliveryStore.deliveryInfo.docHouseNum"
                            type="text"
                            name="docHouseNum"
                            :disabled="loadingToNext"
                        />
                    </template>
                    <template v-if="!docHouseNumValidation.status" #hintBottom>
                        <span>
                            {{ docHouseNumValidation.message }}
                        </span>
                    </template>
                </Field>

                <!-- Поле "Suburb/ District" -->
                <Field v-if="TIWStore.locationIsZA" :is-error="!docDistrictValidation.status">
                    <template #hintTop>
                        <span>
                            {{ "Suburb / District" }}
                        </span>
                    </template>
                    <template #body>
                        <Input
                            v-model="DeliveryStore.deliveryInfo.docDistrict"
                            type="text"
                            name="docDistrict"
                            :disabled="loadingToNext"
                        />
                    </template>
                    <template v-if="!docDistrictValidation.status" #hintBottom>
                        <span>
                            {{ docDistrictValidation.message }}
                        </span>
                    </template>
                </Field>

                <!-- Поле "Postal Code" -->
                <Field v-if="TIWStore.locationIsZA" :is-error="!docPostCodeValidation.status">
                    <template #hintTop>
                        <span>
                            {{ "Postal Code" }}
                        </span>
                    </template>
                    <template #body>
                        <Input
                            v-model="DeliveryStore.deliveryInfo.docPostCode"
                            type="text"
                            name="docPostCode"
                            :disabled="loadingToNext"
                        />
                    </template>
                    <template v-if="!docPostCodeValidation.status" #hintBottom>
                        <span>
                            {{ docPostCodeValidation.message }}
                        </span>
                    </template>
                </Field>

                <!-- Поле "Street Name / Name of farm" -->
                <Field
                    v-if="TIWStore.locationIsZA || TIWStore.locationIsMD"
                    :is-error="!docStreetValidation.status"
                >
                    <template #hintTop>
                        <span>
                            {{ useTranslate("tiw__successfulEvaluation__street") }}
                        </span>
                    </template>
                    <template #body>
                        <Input
                            v-model="DeliveryStore.deliveryInfo.docStreet"
                            type="text"
                            name="docStreet"
                            :disabled="loadingToNext"
                        />
                    </template>
                    <template v-if="!docStreetValidation.status" #hintBottom>
                        <span>
                            {{ docStreetValidation.message }}
                        </span>
                    </template>
                </Field>

                <!-- Поле "City / Town" -->
                <Field
                    v-if="TIWStore.locationIsZA || TIWStore.locationIsMD"
                    :is-error="!docCityNameValidation.status"
                >
                    <template #hintTop>
                        <span>
                            {{ useTranslate("tiw__successfulEvaluation__city") }}
                        </span>
                    </template>
                    <template #body>
                        <Input
                            v-model="DeliveryStore.deliveryInfo.docCityName"
                            type="text"
                            name="docCityName"
                            :disabled="loadingToNext"
                        />
                    </template>
                    <template v-if="!docCityNameValidation.status" #hintBottom>
                        <span>
                            {{ docCityNameValidation.message }}
                        </span>
                    </template>
                </Field>

                <!-- Поле "E-mail" -->
                <Field :is-error="!emailValidation.status">
                    <template #hintTop>
                        <span>
                            {{ useTranslate("tiw__successfulEvaluation__email") }}
                        </span>
                    </template>
                    <template #body>
                        <Input
                            v-model="DeliveryStore.deliveryInfo.email"
                            type="text"
                            name="email"
                            :placeholder="emailFieldPlaceholder"
                            :disabled="loadingToNext"
                        />
                    </template>
                    <template v-if="!emailValidation.status" #hintBottom>
                        <span>
                            {{ emailValidation.message }}
                        </span>
                    </template>
                </Field>

                <!-- Поле "Телефон" -->
                <Field :is-error="!phoneValidation.status">
                    <template #hintTop>
                        <span>
                            {{ useTranslate("tiw__successfulEvaluation__phone") }}
                        </span>
                    </template>
                    <template #body>
                        <Input
                            v-model="DeliveryStore.deliveryInfo.phone"
                            v-maska="phoneMaskOptions"
                            type="text"
                            name="phone"
                            :placeholder="TIWStore.phoneMaskPlaceholder"
                            :disabled="loadingToNext"
                        />
                    </template>
                    <template v-if="!phoneValidation.status" #hintBottom>
                        <span>
                            {{ phoneValidation.message }}
                        </span>
                    </template>
                </Field>
            </form>
            <p :class="classes.arrivalDate__title">
                <b>{{ useTranslate("tiw__thanksYou_pleaseSpecifyArrivalDate") }}</b>
            </p>
            <div :class="classes.arrivalDate__items">
                <div
                    v-for="arrivalDate in arrivalDates"
                    :key="arrivalDate.date"
                    :class="[
                        classes.arrivalDate__item,
                        {
                            [classes.active]:
                                arrivalDate.date === DeliveryStore.deliveryInfo.arrivalDate,
                        },
                    ]"
                    @click="setArrivalDate(arrivalDate.date)"
                >
                    <p :class="classes.arrivalDate__item__title">
                        {{ arrivalDate.dateLabel }}
                    </p>
                    <p :class="classes.arrivalDate__item__date">
                        {{ arrivalDate.timeStart }} - {{ arrivalDate.timeEnd }}
                    </p>
                </div>
            </div>
            <Button
                :disabled="!DeliveryStore.isCanNext || loadingToNext"
                :class="classes.buttonClose"
                @click="onNext"
            >
                {{ useTranslate("tiw__successfulEvaluation__continue") }}
            </Button>
        </div>
    </Modal>
</template>

<style module>
.sameAddress {
    margin-top: 12px;
    margin-bottom: 16px;
}

.header {
    display: flex;
    flex-direction: column;
    align-items: center;

    .title {
        margin: 16px 0 0 0 !important;
    }

    .desc {
        font-size: 12px;
    }
}

.arrivalDate__title {
    margin-top: 24px !important;
    margin-bottom: 12px !important;
}

.body {
    .houseApartment {
        display: flex;
        gap: 16px;
    }

    .buttonClose {
        width: 100%;
        margin-top: 16px;
    }

    .arrivalDate__items {
        display: flex;
        gap: 16px;
        flex-wrap: wrap;
    }

    .arrivalDate__item {
        min-width: 120px;
        width: 30%;
        cursor: pointer;
        user-select: none;
        border: 1px solid #d3dce9;
        padding: 12px 16px;
        border-radius: 6px;
        font-size: 14px;
        transition: all 0.2s;

        &.active {
            border-color: #3500be;
        }

        .title {
            font-weight: bold;
        }

        p {
            margin: 0;
        }

        &:hover {
            background: #d3dce957;
        }
    }
}
</style>
